import {Button, Form, Input, Result, message} from 'antd';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {FaEnvelope, FaPaperPlane, FaWhatsapp} from 'react-icons/fa';

const {TextArea} = Input;

const ContactUs = ({data}) => {
  const [form] = Form.useForm();
  const [submittedData, setSubmittedData] = useState(null);

  // Build WhatsApp/Gmail templates
  const buildTemplates = values => {
    const lines = [
      'Hi, I’d like to contact you regarding your furniture services.',
      '',
      `*Name:* ${values.fullName}`,
      `*Phone:* ${values.phone}`,
      `*Email:* ${values.email || 'N/A'}`,
      `*Message:* ${values.message || 'N/A'}`,
      '',
      'Please get in touch with me. Thank you!',
    ];

    const template = lines.join('\n');
    const waText = encodeURIComponent(template);
    const mailSubject = encodeURIComponent('New enquiry from website');
    const mailBody = encodeURIComponent(template);

    return {
      template,
      whatsapp: `https://wa.me/${data.WA_NUMBER}?text=${waText}`,
      gmail: `https://mail.google.com/mail/?view=cm&fs=1&to=${data.EMAIL_TO}&su=${mailSubject}&body=${mailBody}`,
    };
  };

  // Form submit handler
  const onFinish = values => {
    const templates = buildTemplates(values);
    message.success('Form submitted successfully!');
    setSubmittedData(templates);
  };

  // Scroll to top after submission
  useEffect(() => {
    if (submittedData) {
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  }, [submittedData]);

  // Success View
  if (submittedData) {
    return (
      <div className={'w-full flex justify-center items-center bg-primary-50 min-h-screen p-4'}>
        <div className={'bg-white rounded-3xl shadow-lg p-8 max-w-2xl text-center'}>
          <Result
            status={'success'}
            title={'Click below to connect with us directly.'}
            // subTitle={'Click below to connect with us directly.'}
            extra={[
              <Button
                key={'whatsapp'}
                type={'primary'}
                size={'large'}
                className={'!bg-green-500 flex items-center justify-center gap-2 w-full'}
                onClick={() => window.open(submittedData.whatsapp, '_blank')}
              >
                <FaWhatsapp /> Chat on WhatsApp
              </Button>,
              <Button
                key={'gmail'}
                size={'large'}
                className={'flex items-center justify-center gap-2 mt-3 w-full'}
                onClick={() => window.open(submittedData.gmail, '_blank')}
              >
                <FaEnvelope /> Send via Gmail
              </Button>,
            ]}
          />
          <div className={'bg-gray-100 p-4 rounded mt-4 text-left whitespace-pre-wrap text-sm'}>
            <strong>Message Preview:</strong>
            <br />
            {submittedData.template}
          </div>
        </div>
      </div>
    );
  }

  // Contact Form View
  return (
    <div className={'w-full flex justify-center items-center bg-primary-50 py-12 px-4 h-[700px]'}>
      <div className={'max-w-5xl h-full w-full bg-primary-100 rounded-3xl shadow-lg overflow-hidden flex flex-col lg:flex-row'}>
        {/* Left Image */}
        <div className={'lg:w-1/2 w-full h-60 lg:h-auto'}>
          <img
            src={data.image}
            alt={'Contact Us'}
            className={'w-full h-full object-cover'}
          />
        </div>

        {/* Right Form */}
        <div className={'lg:w-1/2 w-full p-8 lg:p-8'}>
          <h2 className={'text-5xl font-semibold text-primary-800 mb-6 text-center playfair-display-medium'}>
            Contact Us
          </h2>
          <Form layout={'vertical'} form={form} onFinish={onFinish} requiredMark={false}>
            <div className={'flex flex-col'}>
              <Form.Item
                label={'Full Name'}
                name={'fullName'}
                rules={[{required: true, message: 'Please enter your name'}]}
              >
                <Input placeholder={'Your full name'} />
              </Form.Item>

              <Form.Item
                label={'Email'}
                name={'email'}
                rules={[{type: 'email', message: 'Please enter a valid email'}]}
              >
                <Input placeholder={'example@email.com'} />
              </Form.Item>
            </div>

            <Form.Item
              label={'Phone Number'}
              name={'phone'}
              rules={[
                {required: true, message: 'Please enter your phone number'},
                {pattern: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit phone number'},
              ]}
            >
              <Input placeholder={'Your phone number'} />
            </Form.Item>

            <Form.Item
              label={'Message'}
              name={'message'}
              rules={[{required: true, message: 'Please enter your message'}]}
            >
              <TextArea rows={3} placeholder={'Type your message here...'} />
            </Form.Item>

            <Form.Item className={'flex justify-center lg:mt-12 mt-4'}>
              <Button
                type={'primary'}
                htmlType={'submit'}
                className={'!bg-primary-700 !text-white w-full h-8 text-base flex items-center justify-center gap-2'}
              >
                <FaPaperPlane /> Submit 
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

ContactUs.propTypes = {
  data: PropTypes.shape({
    image: PropTypes.string,
    WA_NUMBER: PropTypes.string,
    EMAIL_TO: PropTypes.string,
  }).isRequired,
};

export default ContactUs;
